import { DELTA_CARDS } from "@card-data/cards/amex/delta";
import { CREDITS } from "@card-data/credits";
import { schemas } from "@card-data/generated/api.client";
import { WEIGHTS } from "@card-data/weights";

import { DISCONTINUED_OFFERS } from "../../placeholder-offers";
import { REFERRALS } from "../../referral-links";
import { Credit, CreditCardInput } from "../../types";

const PLATINUM_CREDITS: Array<Credit> = [
  /**
   * Travel
   */
  CREDITS.PRECHECK_OR_SIMILAR,
  {
    value: 850,
    description: "Lounge Access",
    weight: WEIGHTS.LOUNGES,
  },
  {
    value: 600,
    description: "Hotel Credit (2x/yr $300)",
    weight: WEIGHTS.TRAVEL_CREDIT_FUNGIBLE,
  },
  {
    value: 250,
    description: "Misc. Hotel Perks",
    weight: 0.4,
  },
  {
    value: 200,
    description: "Marriott/Hilton Gold Status",
    weight: 0.5,
  },
  {
    value: 100,
    description: "Avis/Hertz/National Gold Status",
    weight: 0.5,
  },
  {
    value: 200,
    description: "$15/mo Uber Cash",
    weight: WEIGHTS.RIDESHARE_CREDIT,
  },
  {
    value: 120,
    description: "Uber One Membership",
    weight: WEIGHTS.RIDESHARE_CREDIT,
  },
  {
    value: 200,
    description: "Airline Fee Credit",
    weight: WEIGHTS.AIRLINE_FEE_CREDIT,
  },

  /**
   * Dining & Entertainment
   */
  {
    value: 400,
    description: "Resy Credit ($100/qtr)",
    weight: WEIGHTS.DINING_CREDIT,
  },
  {
    value: 300,
    description: "Digital Entertainment Credit ($25/mo)",
    weight: WEIGHTS.ENTERTAINMENT_CREDIT,
  },

  /**
   * Shopping & Wellness
   */
  {
    value: 300,
    description: "Lululemon Credit",
    weight: 0.2,
  },
  {
    value: 155,
    description: "Walmart+ Credit",
    weight: 0.3,
  },
  {
    value: 100,
    description: "Saks Credit",
    weight: 0.2,
  },
  {
    value: 200,
    description: "Oura Ring Credit",
    weight: 0.2,
  },
  {
    value: 300,
    description: "Equinox Credit",
    weight: 0.2,
  },
];

export const AMERICAN_EXPRESS_CARDS: Array<CreditCardInput> = [
  ...DELTA_CARDS,
  {
    cardId: "a37efd1da4e7f0d468c87d3b6f021250",
    name: "Blue Business Cash",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: true,
    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 2,
    credits: [],
    url: "https://www.americanexpress.com/en-us/business/credit-cards/blue-business-cash/",
    imageUrl: "/images/amex/blue-business-cash.webp",
    offers: [
      {
        amount: [{ amount: 500 }],
        spend: 15_000,
        days: 365,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 250 }],
        spend: 15_000,
        days: 365,
        credits: [],
      },
      {
        amount: [{ amount: 250 }],
        spend: 3000,
        days: 90,
        credits: [],
      },
      {
        amount: [{ amount: 500 }],
        spend: 15_000,
        days: 90,
        credits: [],
      },
    ],
  },
  {
    cardId: "a851f08b98f839a01a7e8fa367d8b8be",
    name: "Blue Business Plus",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: true,
    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 2,
    url: "https://www.americanexpress.com/us/credit-cards/business/business-credit-cards/american-express-blue-business-plus-credit-card-amex/",
    imageUrl: "/images/amex/blue-business-plus.webp",
    credits: [],
    offers: [
      {
        amount: [{ amount: 15_000 }],
        spend: 3000,
        days: 90,
        credits: [],
        referralUrl: REFERRALS.AMERICAN_EXPRESS.BLUE_BUSINESS_PLUS,
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 15_000 }],
        spend: 3000,
        days: 90,
        credits: [],
      },
      {
        amount: [{ amount: 50_000 }],
        spend: 8000,
        days: 90,
        credits: [],
      },
    ],
  },
  {
    cardId: "6980c320c341292b63273e4362601aa8",
    name: "Blue Cash Everyday",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: false,

    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 2,
    url: "https://www.americanexpress.com/us/credit-cards/card/blue-cash-everyday/",
    imageUrl: "/images/amex/blue-cash-everyday.webp",
    credits: [],
    offers: [
      {
        amount: [{ amount: 200 }],
        spend: 2000,
        days: 180,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 200 }],
        spend: 2000,
        days: 180,
        credits: [
          {
            value: 150,
            description: "Dining Credit",
            weight: WEIGHTS.DINING_CREDIT,
          },
        ],
        url: "https://resy.com/amex-offers",
      },
    ],
  },
  {
    cardId: "04abf9273e03fbe626fe80a9ffd53523",
    name: "Blue Cash Preferred",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: false,
    annualFee: 95,
    isAnnualFeeWaived: true,
    universalCashbackPercent: 2,
    url: "https://www.americanexpress.com/us/credit-cards/card/blue-cash-preferred/",
    imageUrl: "/images/amex/blue-cash-preferred.webp",
    credits: [
      {
        value: 7 * 12,
        description: "$7/mo Disney Bundle Credit",
        weight: WEIGHTS.ENTERTAINMENT_CREDIT,
      },
    ],
    offers: [
      {
        amount: [{ amount: 250 }],
        spend: 3000,
        days: 180,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 300 }],
        spend: 3000,
        days: 180,
        credits: [],
        expiration: "2024-06-01",
      },
    ],
  },
  {
    cardId: "b628058009b9f1a24093028750e18112",
    name: "Blue Cash Preferred (Morgan Stanley)",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: false,
    annualFee: 95,
    isAnnualFeeWaived: true,
    universalCashbackPercent: 2,
    url: "https://us.etrade.com/l/morgan-stanley-blue-cash-preferred",
    imageUrl: "/images/amex/blue-cash-preferred-morgan-stanley.png",
    credits: [
      {
        value: 84,
        description: "$7/mo Disney Bundle Credit",
        weight: WEIGHTS.ENTERTAINMENT_CREDIT,
      },
    ],
    offers: [
      {
        amount: [{ amount: 250 }],
        spend: 3000,
        days: 180,
        credits: [],
        details: "Requires a relationship with Morgan Stanley.",
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 400 }],
        spend: 3000,
        days: 180,
        credits: [],
      },
    ],
  },
  {
    cardId: "191f603ca5af9306d5db0fb208e9a646",
    name: "Business Gold",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: true,
    annualFee: 375,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/business/business-credit-cards/american-express-business-gold-card-amex/",
    imageUrl: "/images/amex/business-gold.webp",
    credits: [],
    offers: [
      {
        amount: [{ amount: 200_000 }],
        spend: 15_000,
        days: 90,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 100_000 }],
        spend: 15_000,
        days: 90,
        credits: [],
        expiration: "2026-09-21",
      },
    ],
  },
  {
    cardId: "aff8fcfdc6a69cef4936e1e474820516",
    name: "Business Green",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: true,
    annualFee: 95,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/business/business-credit-cards/american-express-business-green-card-amex/",
    imageUrl: "/images/amex/business-green.webp",
    credits: [],
    offers: [
      {
        amount: [{ amount: 15_000 }],
        spend: 3000,
        days: 90,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 25_000 }],
        spend: 3000,
        days: 90,
        credits: [],
      },
    ],
  },
  {
    cardId: "656a6a4296d40de4c62cc3ec888e5018",
    name: "Business Platinum",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: true,
    annualFee: 695,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/business/business-credit-cards/american-express-business-platinum-credit-card-amex/",
    imageUrl: "/images/amex/business-platinum.webp",
    credits: [
      /**
       * Travel
       */
      CREDITS.PRECHECK_OR_SIMILAR, // card gets Clear+
      {
        value: 850,
        description: "Lounge Access",
        weight: WEIGHTS.LOUNGES,
      },
      {
        value: 600,
        description: "Hotel Credit (2x/yr $300)",
        weight: WEIGHTS.TRAVEL_CREDIT_FUNGIBLE,
      },
      {
        value: 550,
        description: "Misc. Hotel Perks",
        weight: 0.4,
      },
      {
        value: 200,
        description: "Hilton Credit",
        weight: WEIGHTS.TRAVEL_CREDIT_LOYALTY_PROGRAM_SPECIFIC,
      },
      {
        value: 200,
        description: "Airline Fee Credit",
        weight: WEIGHTS.AIRLINE_FEE_CREDIT,
      },
      {
        value: 100,
        description: "Hotel Collection Credit",
        weight: WEIGHTS.TRAVEL_CREDIT_FUNGIBLE,
      },
      {
        value: 200,
        description: "Marriott/Hilton Gold Status",
        weight: 0.5,
      },

      /**
       * Business
       */
      {
        value: 150,
        description:
          "Dell Credit ($150/yr). Also comes w/ $1k bonus on $5k in spend.",
        weight: 0.25,
      },
      {
        value: 250,
        description: "Adobe Credit ($250 w/ $600 purchase).",
        weight: 0.25,
      },
      {
        value: 90,
        description: "Indeed Credit ($90/qtr).",
        weight: 0.25,
      },
      {
        value: 120,
        description: "Wireless Credit ($10/mo).",
        weight: 0.25,
      },
    ],
    offers: [
      {
        amount: [{ amount: 200_000 }],
        spend: 20_000,
        days: 90,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 250_000 }],
        spend: 30_000,
        days: 90,
        credits: [],
        expiration: "2024-03-01",
      },
    ],
  },
  {
    cardId: "27ebb66ce240c9dcf6e1d13d63e319b4",
    name: "Cash Magnet",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: false,

    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1.5,
    url: "https://www.americanexpress.com/us/credit-cards/card/cash-magnet/",
    imageUrl: "/images/amex/cash-magnet.webp",
    credits: [],
    offers: [
      // this card doesn't take new applications, I think
      // seemingly no offers right now
      // {
      //   amount: [{ amount: 200 }],
      //   spend: 2000,
      //   days: 180,
      //   credits: [],
      //   url: "https://www.americanexpress.com/us/credit-cards/card/cash-magnet/",
      // },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 300 }],
        spend: 2500,
        days: 180,
        credits: [],
      },
      {
        amount: [{ amount: 200 }],
        spend: 1000,
        days: 180,
        credits: [],
      },
    ],
  },

  {
    cardId: "54fddf9e59dfad730320eb65a04b7ea5",
    name: "Everyday",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: false,
    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://global.americanexpress.com/card-benefits/view-all/amex-everyday",
    imageUrl: "/images/amex/everyday.png",
    credits: [],

    discontinued: true,
    offers: DISCONTINUED_OFFERS,
    historicalOffers: DISCONTINUED_OFFERS,
  },
  {
    cardId: "84c122e1077e2929b1f563bd143ce82e",
    name: "Everyday Preferred",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: false,
    annualFee: 95,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/card/amex-everyday-preferred/",
    imageUrl: "/images/amex/everyday-preferred.jpg",
    credits: [],

    discontinued: true,
    offers: DISCONTINUED_OFFERS,
    historicalOffers: DISCONTINUED_OFFERS,
  },
  {
    cardId: "cafe43d37256bec116dff4be6cced2cf",
    name: "Gold",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: false,
    annualFee: 350,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/card/gold-card/",
    imageUrl: "/images/amex/gold.webp",
    credits: [
      {
        description: "$10/mo credit for Uber",
        value: 120,
        weight: WEIGHTS.RIDESHARE_CREDIT,
      },
      {
        description:
          "$10/mo credit for Grubhub, Cheesecake Factory, Gold Belly, Wine.com, Milk, Shake Shack",
        value: 120,
        weight: 0.5,
      },
      {
        description: "$7/mo credit for Dunkin",
        value: 7 * 12,
        weight: 0.4,
      },
      {
        description: "Semi-annual $50 Resy credit",
        value: 50 * 2,
        weight: WEIGHTS.DINING_CREDIT,
      },
    ],
    offers: [
      {
        amount: [{ amount: 100_000 }],
        spend: 8000,
        days: 180,
        credits: [],
      },
    ],

    historicalOffers: [
      {
        amount: [{ amount: 60_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        expiration: "2026-04-01",
      },
      {
        amount: [{ amount: 90_000 }],
        spend: 4000,
        days: 180,
        credits: [],
        expiration: "2024-03-01",
      },
    ],
  },
  {
    cardId: "ad9a96ed5f730a79d0171f1f8ca92d0c",
    name: "Green",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: false,
    annualFee: 150,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/card/green/",
    imageUrl: "/images/amex/green.webp",
    credits: [
      CREDITS.PRECHECK_OR_SIMILAR,
      {
        value: 100,
        description: "Lounge Access Credit",
        weight: WEIGHTS.LOUNGES,
      },
    ],
    offers: [
      {
        amount: [{ amount: 40_000 }],
        spend: 3000,
        days: 180,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 40_000 }],
        spend: 3000,
        days: 180,
        credits: [],
      },
    ],
  },
  {
    cardId: "0710239dc6fbd92077bce7118572e217",
    name: "Hilton Honors",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.HILTON,
    isBusiness: false,
    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.hilton.com/en/hilton-honors/credit-cards/",
    imageUrl: "/images/amex/hilton-honors.png",
    credits: [],
    offers: [
      {
        amount: [{ amount: 70_000 }],
        spend: 2000,
        days: 180,
        credits: [
          {
            value: 1 * 30_000,
            description: "1x Free Night Certificate, ballpark ~30k per.",
            weight: WEIGHTS.FREE_NIGHT_CREDIT,
            currency: schemas.CurrenciesEnum.enum.HILTON,
          },
        ],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 80_000 }],
        spend: 2000,
        days: 180,
        credits: [],
        expiration: "2026-09-21",
      },
      {
        amount: [{ amount: 100_000 }],
        spend: 2000,
        days: 180,
        credits: [
          {
            value: 100,
            description: "$100 statement credit",
            currency: schemas.CurrenciesEnum.enum.USD,
            weight: 1,
          },
        ],
        expiration: "2026-07-29",
      },
      {
        amount: [{ amount: 100_000 }],
        spend: 5000,
        days: 180,
        credits: [],
        expiration: "2025-01-01",
      },
    ],
  },
  {
    cardId: "59c1e4c423d078c91e9b7c51d72e893d",
    name: "Hilton Honors Aspire",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.HILTON,
    isBusiness: false,
    annualFee: 550,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.hilton.com/en/hilton-honors/credit-cards/",
    imageUrl: "/images/amex/hilton-honors-aspire.png",
    credits: [
      {
        value: 250,
        description: "Hilton Resort Credit",
        weight: 0.8,
      },
    ],
    offers: [
      {
        amount: [{ amount: 200_000 }],
        spend: 6000,
        days: 180,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 150_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        expiration: "2026-09-21",
      },
      {
        amount: [{ amount: 175_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        expiration: "2026-07-29",
      },
      {
        amount: [{ amount: 175_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        expiration: "2026-04-15",
      },
      {
        amount: [{ amount: 180_000 }],
        spend: 4000,
        days: 180,
        credits: [],
        expiration: "2023-12-01",
      },
    ],
  },
  {
    cardId: "434df114291a974c876d58cf79158376",
    name: "Hilton Honors Business",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.HILTON,
    isBusiness: true,
    annualFee: 175,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 5,
    url: "https://www.hilton.com/en/hilton-honors/credit-cards/",
    imageUrl: "/images/amex/hilton-honors-business.png",
    credits: [
      {
        currency: "HILTON",
        value: 240,
        weight: 0.5,
        description: "$60 Hilton credit per quarter.",
      },
    ],
    offers: [
      {
        amount: [{ amount: 150_000 }],
        spend: 8000,
        days: 180,
        credits: [
          {
            value: 1 * 30_000,
            description: "1x Free Night Certificate, ballpark ~30k per.",
            weight: WEIGHTS.FREE_NIGHT_CREDIT,
            currency: schemas.CurrenciesEnum.enum.HILTON,
          },
        ],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 130_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        expiration: "2026-09-21",
      },
      {
        amount: [{ amount: 130_000 }],
        spend: 6000,
        days: 180,
        credits: [
          {
            value: 175,
            description: "First year annual fee waived",
            weight: 1,
          },
        ],
        expiration: "2026-07-29",
      },
      {
        amount: [{ amount: 175_000 }],
        spend: 8000,
        days: 180,
        credits: [],
        expiration: "2026-04-15",
      },
      {
        amount: [{ amount: 175_000 }],
        spend: 6_000,
        days: 180,
        credits: [],
        expiration: "2026-02-28",
      },
    ],
  },
  {
    cardId: "f6c6aa50b533b758df7be5151d805571",
    name: "Hilton Honors Surpass",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.HILTON,
    isBusiness: false,
    annualFee: 95,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.hilton.com/en/hilton-honors/credit-cards/",
    imageUrl: "/images/amex/hilton-honors-surpass.jpg",
    credits: [],
    offers: [
      {
        amount: [{ amount: 130_000 }],
        spend: 3000,
        days: 180,
        credits: [
          {
            value: 1 * 30_000,
            description: "1x Free Night Certificate, ballpark ~30k per.",
            weight: WEIGHTS.FREE_NIGHT_CREDIT,
            currency: schemas.CurrenciesEnum.enum.HILTON,
          },
        ],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 130_000 }],
        spend: 3000,
        days: 180,
        credits: [],
        expiration: "2026-09-21",
      },
      {
        amount: [{ amount: 130_000 }],
        spend: 3000,
        days: 180,
        credits: [
          {
            value: 95,
            description: "First year annual fee waived",
            weight: 1,
          },
        ],
        expiration: "2026-07-29",
      },
      {
        amount: [{ amount: 130_000 }],
        spend: 3000,
        days: 180,
        credits: [
          {
            value: 1 * 30_000,
            description: "1x FNC, ballpark ~30k per.",
            weight: WEIGHTS.FREE_NIGHT_CREDIT,
            currency: schemas.CurrenciesEnum.enum.HYATT,
          },
        ],
        expiration: "2026-04-15",
      },
      {
        amount: [{ amount: 170_000 }],
        spend: 2000,
        days: 180,
        credits: [],
        details: "2024-02-01",
      },
    ],
  },
  {
    cardId: "4d2115812f09d194440ef99b1a5210cd",
    name: "Marriott Bonvoy Brilliant",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.MARRIOTT,
    isBusiness: false,
    annualFee: 650,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/card/marriott-bonvoy-brilliant/",
    imageUrl: "/images/amex/marriott-bonvoy-brilliant.webp",
    credits: [
      {
        value: 300,
        description: "$25/mo Dining Credit",
        weight: WEIGHTS.DINING_CREDIT,
      },
    ],
    offers: [
      {
        amount: [{ amount: 150_000 }],
        spend: 8000,
        days: 180,
        credits: [
          {
            value: 250,
            description: "$250 statement credit",
            currency: schemas.CurrenciesEnum.enum.USD,
            weight: 1,
          },
        ],
        details: "100k @ $6k + extra 50k @ $8k, both within 180 days.",
        expiration: "2026-09-30",
      },
      {
        amount: [{ amount: 100_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        details: "100k @ $6k + extra 50k @ $8k, both within 180 days.",
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 200_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        expiration: "2026-05-13",
      },
      {
        amount: [{ amount: 100_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        expiration: "2026-03-21",
      },
      {
        amount: [{ amount: 200_000 }],
        spend: 6000,
        days: 180,
        credits: [],
        expiration: "2023-07-01",
      },
    ],
  },
  {
    cardId: "1129280627d633940e575deabdaa105f",
    name: "Marriott Bonvoy Business",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.MARRIOTT,
    isBusiness: true,
    annualFee: 125,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/business/business-credit-cards/amex-marriott-bonvoy-business-credit-card/",
    imageUrl: "/images/amex/marriott-bonvoy-business.webp",
    credits: [],
    offers: [
      {
        amount: [{ amount: 150_000 }],
        spend: 8000,
        days: 180,
        credits: [
          {
            value: 125,
            description: "$125 statement credit",
            currency: schemas.CurrenciesEnum.enum.USD,
            weight: 1,
          },
        ],
      },
      {
        amount: [{ amount: 0 }],
        spend: 3000,
        days: 90,
        credits: [
          {
            description: "3x FNC, <= 50k per",
            weight: WEIGHTS.FREE_NIGHT_CREDIT,
            value: 150_000,
            currency: schemas.CurrenciesEnum.enum.MARRIOTT,
          },
        ],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 0 }],
        spend: 3000,
        days: 90,
        credits: [
          {
            description: "3x FNC, <= 50k per",
            weight: WEIGHTS.FREE_NIGHT_CREDIT,
            value: 150_000,
            currency: schemas.CurrenciesEnum.enum.MARRIOTT,
          },
        ],
      },
      {
        amount: [{ amount: 125_000 }],
        credits: [],
        spend: 8000,
        days: 90,
      },
    ],
  },
  {
    cardId: "ec1872dfd8a65491a33bae2da4dd487f",
    name: "Platinum",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: false,
    annualFee: 895,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/card/platinum/",
    imageUrl: "/images/amex/platinum.webp",
    credits: PLATINUM_CREDITS,
    offers: [
      {
        amount: [{ amount: 175_000 }],
        spend: 12_000,
        days: 180,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 175_000 }],
        spend: 8000,
        days: 180,
        credits: [],
        expiration: "2026-09-07",
      },
      {
        amount: [{ amount: 100_000 }],
        spend: 8000,
        days: 180,
        credits: [],
        expiration: "2026-02-01",
      },
      {
        amount: [{ amount: 175_000 }],
        spend: 8000,
        days: 180,
        credits: [],
        expiration: "2026-02-01",
      },
    ],
  },
  {
    cardId: "7eb93f5f9ab64e1c12b82ec8822c25ef",
    name: "Platinum (Schwab)",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: false,
    annualFee: 895,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.schwab.com/credit-cards/platinum-card",
    imageUrl: "/images/amex/platinum-schwab.webp",
    credits: PLATINUM_CREDITS,
    offers: [
      {
        amount: [{ amount: 80_000 }],
        spend: 8000,
        days: 180,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 125_000 }],
        credits: [],
        spend: 8000,
        days: 180,
        expiration: "2025-01-08", // ref: https://www.doctorofcredit.com/american-express-charles-schwab-125000-point-bonus/
      },
    ],
  },
  {
    cardId: "ab55a96910ffc7f31fc1605c99430ec5",
    name: "Platinum (Morgan Stanley)",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.AMERICAN_EXPRESS,
    isBusiness: false,
    annualFee: 895,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://us.etrade.com/l/morgan-stanley-platinum",
    imageUrl: "/images/amex/platinum-morgan-stanley.webp",
    credits: PLATINUM_CREDITS,
    offers: [
      {
        amount: [{ amount: 80_000 }],
        spend: 8000,
        days: 180,
        credits: [],
        details: "Requires a relationship with Morgan Stanley.",
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 125_000 }],
        spend: 8000,
        days: 180,
        credits: [],
        details: "Requires a relationship with Morgan Stanley.",
        expiration: "2025-11-12",
      },
    ],
  },
  {
    cardId: "653b14a655f4edc9bfc0332e9370e139",
    name: "Marriott Bonvoy Bevy",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.MARRIOTT,
    isBusiness: false,
    annualFee: 250,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/card/marriott-bonvoy-bevy/",
    imageUrl: "/images/amex/marriott-bonvoy-bevy.png",
    credits: [],
    offers: [
      {
        amount: [{ amount: 125_000 }],
        spend: 5000,
        days: 180,
        credits: [
          {
            value: 150,
            description: "$150 statement credit",
            currency: schemas.CurrenciesEnum.enum.USD,
            weight: 1,
          },
        ],
        expiration: "2026-09-30",
      },
      {
        amount: [{ amount: 85_000 }],
        spend: 7000,
        days: 180,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 175_000 }],
        spend: 5000,
        days: 180,
        credits: [],
        expiration: "2026-05-13",
      },
      {
        amount: [{ amount: 85_000 }],
        spend: 7000,
        days: 180,
        credits: [],
        expiration: "2026-03-21",
      },
      {
        amount: [{ amount: 175_000 }],
        spend: 5000,
        days: 180,
        credits: [],
        expiration: "2023-07-01",
      },
    ],
  },
  {
    cardId: "9bf8d331ae97a8ddc47ef3d22dcaf213",
    name: "Lowes",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: true,
    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/business/business-credit-cards/lowes-credit-cards/",
    imageUrl: "/images/amex/lowes-business.webp",
    credits: [],
    offers: [
      // No offers seemingly available right now
    ],
    historicalOffers: [
      {
        amount: [{ amount: 0 }],
        spend: 0,
        days: 90,
        credits: [
          {
            value: 100,
            description: "Automatic statement credit",
            weight: 1,
          },
        ],
      },
    ],
  },
  {
    cardId: "a3b091353bd1a45ad58f75e5287f1fd6",
    name: "Schwab Investor",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: false,
    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.schwab.com/public/schwab/investing/accounts_products/credit_cards",
    imageUrl: "/images/amex/schwab-investor.webp",
    credits: [],
    offers: [
      {
        amount: [{ amount: 200 }],
        spend: 1000,
        days: 90,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 300 }],
        spend: 1000,
        days: 90,
        credits: [],
      },
    ],
  },
  {
    cardId: "15cc2b7c31596c0112cfba30180ab739",
    name: "Amazon Business",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: true,
    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.americanexpress.com/us/credit-cards/business/business-credit-cards/amazon-business-card/",
    imageUrl: "/images/amex/amazon-business.avif",
    credits: [],
    offers: [
      // Amex pulled this from new applications; the card moves to U.S. Bank (Mastercard) on 2026-08-14.
    ],
    historicalOffers: [
      {
        amount: [{ amount: 100 }],
        spend: 0.01,
        days: 90,
        credits: [],
        expiration: "2026-08-04",
      },
      {
        amount: [{ amount: 200 }],
        spend: 0.01,
        days: 90,
        credits: [],
      },
      {
        amount: [{ amount: 400 }],
        spend: 4000,
        days: 90,
        url: "https://www.amazon.com/Amazon-Business-American-Express-Card/dp/B07984JN3L",
        credits: [],
      },
    ],
  },
  {
    cardId: "6ca9fd6caab5364f72928dd5ba04bb58",
    name: "Rakuten",
    issuer: schemas.IssuersEnum.enum.AMERICAN_EXPRESS,
    network: schemas.NetworksEnum.enum.AMERICAN_EXPRESS,
    currency: schemas.CurrenciesEnum.enum.USD,
    isBusiness: true,
    annualFee: 0,
    isAnnualFeeWaived: false,
    universalCashbackPercent: 1,
    url: "https://www.rakuten.com/american-express-card/",
    imageUrl: "/images/amex/rakuten.jpg",
    credits: [],
    offers: [
      {
        amount: [{ amount: 25 }],
        spend: 500,
        days: 90,
        credits: [],
      },
    ],
    historicalOffers: [
      {
        amount: [{ amount: 25 }],
        spend: 500,
        days: 90,
        credits: [],
      },
    ],
  },
];
